import { create } from 'zustand';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { DMZConfig, DMZConfigInsert, DMZConfigUpdate, FirewallRule, FirewallRuleInsert, FirewallRuleUpdate } from '@/types/dmzConfig';

interface DMZConfigState {
  configs: DMZConfig[];
  loading: boolean;
  loadConfigs: () => Promise<void>;
  addConfig: (config: DMZConfigInsert) => Promise<DMZConfig | null>;
  updateConfig: (id: string, updates: DMZConfigUpdate) => Promise<void>;
  deleteConfig: (id: string) => Promise<void>;
  getConfigById: (id: string) => DMZConfig | undefined;
  getConfigsBySiteId: (siteId: string) => DMZConfig[];
  getConfigsByZoneId: (zoneId: string) => DMZConfig[];
  getConfigsBySystemId: (systemId: string) => DMZConfig[];
  getConfigsByCloudServiceId: (cloudServiceId: string) => DMZConfig[];
  // Firewall rules
  addRule: (rule: FirewallRuleInsert) => Promise<FirewallRule | null>;
  updateRule: (id: string, updates: FirewallRuleUpdate) => Promise<void>;
  deleteRule: (id: string) => Promise<void>;
  loadRulesForConfig: (dmzConfigId: string) => Promise<FirewallRule[]>;
}

export const useDMZConfigStore = create<DMZConfigState>()((set, get) => ({
  configs: [],
  loading: false,

  loadConfigs: async () => {
    set({ loading: true });
    try {
      const { data, error } = await supabase
        .from('dmz_configs')
        .select('*')
        .order('label');

      if (error) throw error;

      // Load rules for each config
      const configsWithRules = await Promise.all(
        (data || []).map(async (config: any) => {
          const rules = await get().loadRulesForConfig(config.id);
          return { ...config, rules };
        })
      );

      set({ configs: configsWithRules as DMZConfig[] });
    } catch (error) {
      console.error('Error loading DMZ configs:', error);
      toast.error('Failed to load DMZ configurations');
    } finally {
      set({ loading: false });
    }
  },

  addConfig: async (config) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user?.id) {
        toast.error('User not authenticated');
        return null;
      }

      const { data: profile } = await supabase
        .from('profiles')
        .select('org_id')
        .eq('user_id', userData.user.id)
        .maybeSingle();

      if (!profile?.org_id) {
        toast.error('No organization found');
        return null;
      }

      const { data, error } = await supabase
        .from('dmz_configs')
        .insert({
          ...config,
          org_id: profile.org_id,
        })
        .select()
        .single();

      if (error) throw error;

      await get().loadConfigs();
      toast.success('DMZ configuration created');
      return data as DMZConfig;
    } catch (error: any) {
      console.error('Error creating DMZ config:', error);
      toast.error(error.message || 'Failed to create DMZ configuration');
      return null;
    }
  },

  updateConfig: async (id, updates) => {
    try {
      const { error } = await supabase
        .from('dmz_configs')
        .update(updates)
        .eq('id', id);

      if (error) throw error;

      await get().loadConfigs();
      toast.success('DMZ configuration updated');
    } catch (error: any) {
      console.error('Error updating DMZ config:', error);
      toast.error(error.message || 'Failed to update DMZ configuration');
    }
  },

  deleteConfig: async (id) => {
    try {
      const { error } = await supabase
        .from('dmz_configs')
        .delete()
        .eq('id', id);

      if (error) throw error;

      set(state => ({
        configs: state.configs.filter(c => c.id !== id)
      }));
      toast.success('DMZ configuration deleted');
    } catch (error: any) {
      console.error('Error deleting DMZ config:', error);
      toast.error(error.message || 'Failed to delete DMZ configuration');
    }
  },

  getConfigById: (id) => {
    return get().configs.find(c => c.id === id);
  },

  getConfigsBySiteId: (siteId) => {
    return get().configs.filter(c => c.site_id === siteId && !c.zone_id && !c.system_id && !c.cloud_service_id);
  },

  getConfigsByZoneId: (zoneId) => {
    return get().configs.filter(c => c.zone_id === zoneId && !c.system_id && !c.cloud_service_id);
  },

  getConfigsBySystemId: (systemId) => {
    return get().configs.filter(c => c.system_id === systemId && !c.cloud_service_id);
  },

  getConfigsByCloudServiceId: (cloudServiceId) => {
    return get().configs.filter(c => c.cloud_service_id === cloudServiceId);
  },

  loadRulesForConfig: async (dmzConfigId) => {
    try {
      const { data, error } = await supabase
        .from('firewall_rules')
        .select('*')
        .eq('dmz_config_id', dmzConfigId)
        .order('rule_order');

      if (error) throw error;
      return (data || []) as FirewallRule[];
    } catch (error) {
      console.error('Error loading firewall rules:', error);
      return [];
    }
  },

  addRule: async (rule) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user?.id) {
        toast.error('User not authenticated');
        return null;
      }

      const { data: profile } = await supabase
        .from('profiles')
        .select('org_id')
        .eq('user_id', userData.user.id)
        .maybeSingle();

      if (!profile?.org_id) {
        toast.error('No organization found');
        return null;
      }

      const { data, error } = await supabase
        .from('firewall_rules')
        .insert({
          ...rule,
          org_id: profile.org_id,
        })
        .select()
        .single();

      if (error) throw error;

      await get().loadConfigs();
      return data as FirewallRule;
    } catch (error: any) {
      console.error('Error adding firewall rule:', error);
      toast.error(error.message || 'Failed to add firewall rule');
      return null;
    }
  },

  updateRule: async (id, updates) => {
    try {
      const { error } = await supabase
        .from('firewall_rules')
        .update(updates)
        .eq('id', id);

      if (error) throw error;

      await get().loadConfigs();
    } catch (error: any) {
      console.error('Error updating firewall rule:', error);
      toast.error(error.message || 'Failed to update firewall rule');
    }
  },

  deleteRule: async (id) => {
    try {
      const { error } = await supabase
        .from('firewall_rules')
        .delete()
        .eq('id', id);

      if (error) throw error;

      await get().loadConfigs();
    } catch (error: any) {
      console.error('Error deleting firewall rule:', error);
      toast.error(error.message || 'Failed to delete firewall rule');
    }
  },
}));
